import { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, ExternalLink, Download } from 'lucide-react';

// ─── PDF.js loader singleton (CDN, no npm install) ───────────────────────────
let _pdfJsReady = false;
let _pdfJsPromise: Promise<void> | null = null;
export function loadPdfJs(): Promise<void> {
  if (_pdfJsReady) return Promise.resolve();
  if (_pdfJsPromise) return _pdfJsPromise;
  _pdfJsPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    s.onload = () => {
      (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      _pdfJsReady = true;
      resolve();
    };
    s.onerror = () => reject(new Error('PDF.js CDN load failed'));
    document.head.appendChild(s);
  });
  return _pdfJsPromise;
}

// ─── PdfViewerButton component ───────────────────────────────────────────────
// Lightweight button that opens the full-screen reader without showing thumbnails.
// Used in compact lists (e.g. Compliance PM report rows).
export function PdfViewerButton({ pdfUrl, label, triggerClassName, triggerIcon }: { pdfUrl: string; label: string; triggerClassName?: string; triggerIcon?: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [scale, setScale] = useState(1.8);
  const [currentPage, setCurrentPage] = useState(1);
  const pageRefs = useRef<(HTMLCanvasElement | null)[]>([]);

  function handleOpen() {
    setOpen(true);
    if (pdfDoc) return;
    setLoading(true);
    setLoadError(false);
    console.log('[PdfViewer] loading:', pdfUrl);
    const token = localStorage.getItem('accessToken');
    const httpHeaders = token ? { Authorization: `Bearer ${token}` } : {};
    loadPdfJs()
      .then(() => (window as any).pdfjsLib.getDocument({ url: pdfUrl, httpHeaders, withCredentials: false }).promise)
      .then((doc: any) => { setPdfDoc(doc); setLoading(false); })
      .catch((err: any) => {
        console.error('[PdfViewer] load error:', err?.message ?? err, 'url:', pdfUrl);
        setLoadError(true);
        setLoading(false);
      });
  }

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', handler);
    return () => { window.removeEventListener('keydown', handler); document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (!open || !pdfDoc) return;
    const numPages = pdfDoc.numPages;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < numPages; i++) {
        if (cancelled) return;
        const canvas = pageRefs.current[i];
        if (!canvas) continue;
        try {
          const page = await pdfDoc.getPage(i + 1);
          if (cancelled) return;
          const vp = page.getViewport({ scale });
          canvas.width = vp.width;
          canvas.height = vp.height;
          const ctx = canvas.getContext('2d');
          if (ctx) page.render({ canvasContext: ctx, viewport: vp });
        } catch { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
  }, [open, pdfDoc, scale]);

  useEffect(() => {
    if (!open || !pdfDoc) return;
    setCurrentPage(1);
    const observer = new IntersectionObserver(
      (entries) => {
        let bestEntry: IntersectionObserverEntry | null = null;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!bestEntry || entry.intersectionRatio > bestEntry.intersectionRatio) {
              bestEntry = entry;
            }
          }
        });
        if (bestEntry) {
          const idx = pageRefs.current.indexOf((bestEntry as IntersectionObserverEntry).target as HTMLCanvasElement);
          if (idx !== -1) setCurrentPage(idx + 1);
        }
      },
      { threshold: [0.1, 0.3, 0.5] }
    );
    const timer = setTimeout(() => {
      pageRefs.current.forEach((canvas) => { if (canvas) observer.observe(canvas); });
    }, 150);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [open, pdfDoc]);

  const numPages = pdfDoc?.numPages ?? 0;

  return (
    <>
      {triggerClassName ? (
        <button type="button" onClick={handleOpen} className={triggerClassName}>
          {triggerIcon}{label}
        </button>
      ) : (
        <button
          type="button"
          onClick={handleOpen}
          style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', color: '#e2e8f0', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: 0, fontSize: '0.75rem' }}>
          {label}
        </button>
      )}
      {open && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9995, background: 'rgba(0,0,0,0.95)', overflow: 'auto' }}
          onClick={() => setOpen(false)}
        >
          <div
            style={{ position: 'sticky', top: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', padding: '8px 16px', display: 'flex', gap: 12, zIndex: 10, alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', fontWeight: 500, minWidth: 90 }}>
              Page {currentPage} of {numPages || '…'}
            </span>
            <button onClick={() => setScale((s) => Math.min(s + 0.2, 3.0))}
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#fff', fontSize: '0.875rem', fontWeight: 600 }}>+</button>
            <button onClick={() => setScale((s) => Math.max(s - 0.2, 0.8))}
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#fff', fontSize: '0.875rem', fontWeight: 600 }}>−</button>
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8125rem' }}>{Math.round(scale * 100)}%</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(pdfUrl, '_blank', 'noopener,noreferrer');
              }}
              style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 4, padding: 0 }}>
              <ExternalLink size={13} /> Open
            </button>
            <a
              href={`${pdfUrl}&download=true`}
              download
              onClick={(e) => e.stopPropagation()}
              style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 4, padding: 0, textDecoration: 'none' }}>
              <Download size={13} /> Download
            </a>
            <button onClick={() => setOpen(false)}
              style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center' }}>
              <X size={18} />
            </button>
          </div>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
              <div className="loading-spinner" style={{ width: 24, height: 24 }} />
            </div>
          ) : loadError ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 12, color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem' }}>
              <span>Could not load PDF preview.</span>
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                style={{ color: '#60a5fa', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                <ExternalLink size={14} /> Open file directly
              </a>
            </div>
          ) : (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 16, alignItems: 'center' }}
              onClick={(e) => e.stopPropagation()}
            >
              {numPages > 0 && Array.from({ length: numPages }, (_, i) => (
                <canvas
                  key={i}
                  ref={(el) => { pageRefs.current[i] = el; }}
                  style={{ display: 'block', boxShadow: '0 4px 24px rgba(0,0,0,0.6)', borderRadius: 4, maxWidth: '100%' }}
                />
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

// ─── PdfThumbnailRow component ────────────────────────────────────────────────
export function PdfThumbnailRow({ pdfUrl }: { pdfUrl: string }) {
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [readerOpen, setReaderOpen] = useState(false);
  const [readerScale, setReaderScale] = useState(1.8);
  const [readerCurrentPage, setReaderCurrentPage] = useState(1);
  const thumbRef0 = useRef<HTMLCanvasElement>(null);
  const thumbRef1 = useRef<HTMLCanvasElement>(null);
  const thumbRef2 = useRef<HTMLCanvasElement>(null);
  const thumbRefs = [thumbRef0, thumbRef1, thumbRef2];
  const readerPageRefs = useRef<(HTMLCanvasElement | null)[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    setPdfDoc(null);
    loadPdfJs()
      .then(() => {
        if (cancelled) return null;
        return (window as any).pdfjsLib.getDocument({ url: pdfUrl, withCredentials: false }).promise;
      })
      .then((doc: any) => {
        if (cancelled || !doc) return;
        setPdfDoc(doc);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) { setLoadError(true); setLoading(false); }
      });
    return () => { cancelled = true; };
  }, [pdfUrl]);

  useEffect(() => {
    if (!pdfDoc) return;
    const count = Math.min(pdfDoc.numPages, 3);
    for (let i = 0; i < count; i++) {
      const canvas = thumbRefs[i].current;
      if (!canvas) continue;
      const idx = i;
      pdfDoc.getPage(idx + 1).then((page: any) => {
        const c = thumbRefs[idx].current;
        if (!c) return;
        const vp = page.getViewport({ scale: 0.315 });
        c.width = vp.width;
        c.height = vp.height;
        const ctx = c.getContext('2d');
        if (ctx) page.render({ canvasContext: ctx, viewport: vp });
      }).catch(() => {/* ignore */});
    }
  }, [pdfDoc]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!readerOpen) return;
    document.body.style.overflow = 'hidden';
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setReaderOpen(false); };
    window.addEventListener('keydown', handler);
    return () => { window.removeEventListener('keydown', handler); document.body.style.overflow = ''; };
  }, [readerOpen]);

  useEffect(() => {
    if (!readerOpen || !pdfDoc) return;
    const numPages = pdfDoc.numPages;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < numPages; i++) {
        if (cancelled) return;
        const canvas = readerPageRefs.current[i];
        if (!canvas) continue;
        try {
          const page = await pdfDoc.getPage(i + 1);
          if (cancelled) return;
          const vp = page.getViewport({ scale: readerScale });
          canvas.width = vp.width;
          canvas.height = vp.height;
          const ctx = canvas.getContext('2d');
          if (ctx) page.render({ canvasContext: ctx, viewport: vp });
        } catch { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
  }, [readerOpen, pdfDoc, readerScale]);

  useEffect(() => {
    if (!readerOpen || !pdfDoc) return;
    setReaderCurrentPage(1);
    const observer = new IntersectionObserver(
      (entries) => {
        let bestEntry: IntersectionObserverEntry | null = null;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!bestEntry || entry.intersectionRatio > bestEntry.intersectionRatio) {
              bestEntry = entry;
            }
          }
        });
        if (bestEntry) {
          const idx = readerPageRefs.current.indexOf((bestEntry as IntersectionObserverEntry).target as HTMLCanvasElement);
          if (idx !== -1) setReaderCurrentPage(idx + 1);
        }
      },
      { threshold: [0.1, 0.3, 0.5] }
    );
    const timer = setTimeout(() => {
      readerPageRefs.current.forEach((canvas) => { if (canvas) observer.observe(canvas); });
    }, 150);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [readerOpen, pdfDoc]);

  if (loading) {
    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ width: 140, height: 186, borderRadius: 4, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="loading-spinner" style={{ width: 14, height: 14 }} />
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6, fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <ExternalLink size={11} /> Open full PDF →
        </button>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Could not load preview</div>
        <button
          type="button"
          onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <ExternalLink size={11} /> Open full PDF →
        </button>
      </div>
    );
  }

  const numPages = pdfDoc?.numPages ?? 0;
  const thumbCount = Math.min(numPages, 3);

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        {Array.from({ length: thumbCount }, (_, i) => (
          <canvas
            key={i}
            ref={thumbRefs[i]}
            style={{ width: 140, height: 186, borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', flexShrink: 0 }}
            onClick={() => { setReaderCurrentPage(1); setReaderOpen(true); }}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6, fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
        <ExternalLink size={11} /> Open full PDF →
      </button>

      {readerOpen && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9995, background: 'rgba(0,0,0,0.95)', overflow: 'auto' }}
          onClick={() => setReaderOpen(false)}
        >
          <div
            style={{ position: 'sticky', top: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', padding: '8px 16px', display: 'flex', gap: 12, zIndex: 10, alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', fontWeight: 500, minWidth: 90 }}>
              Page {readerCurrentPage} of {numPages}
            </span>
            <button
              onClick={() => setReaderScale((s) => Math.min(s + 0.2, 3.0))}
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#fff', fontSize: '0.875rem', fontWeight: 600 }}
            >+</button>
            <button
              onClick={() => setReaderScale((s) => Math.max(s - 0.2, 0.8))}
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#fff', fontSize: '0.875rem', fontWeight: 600 }}
            >−</button>
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8125rem' }}>
              {Math.round(readerScale * 100)}%
            </span>
            <button
              onClick={() => setReaderOpen(false)}
              style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center' }}
            >
              <X size={18} />
            </button>
          </div>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 16, alignItems: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >
            {Array.from({ length: numPages }, (_, i) => (
              <canvas
                key={i}
                ref={(el) => { readerPageRefs.current[i] = el; }}
                style={{ display: 'block', boxShadow: '0 4px 24px rgba(0,0,0,0.6)', borderRadius: 4, maxWidth: '100%' }}
              />
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
